import React, { ReactNode } from 'react';

import { ColorSchema } from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { applyColorSchema, makeClassNames } from '../../../utils/styles';
import Cta from '../../atoms/cta/cta';
import IconMotorDashboard from '../../atoms/icons/icon-motor-dashboard';
import Text from '../../atoms/text/text';

import './courtesy-message.scss';

export interface ICourtesyMessage extends IComponent {
  cta?: {
    href?: string;
    text?: string;
  };
  text?: string;
  date?: string;
  backgroundIcon?: ReactNode;
  cardLayout?: boolean;
  colorSchema?: ColorSchema;
  dataSpaced?: boolean;
}

export const defaultBackgroundIcon = () => <IconMotorDashboard size="xxlarge" color="medium-light" />;

const classNames = ({
  colorSchema,
  cardLayout,
  cta,
}: {
  colorSchema: ICourtesyMessage['colorSchema'];
  cardLayout: ICourtesyMessage['cardLayout'];
  cta: ICourtesyMessage['cta'];
}) =>
  makeClassNames({
    CourtesyMessage: [
      'courtesy-message',
      cta && 'courtesy-message--withCta',
      colorSchema && `${applyColorSchema('courtesy-message', colorSchema)}`,
      cardLayout && 'courtesy-message--card-layout',
    ],
    CourtesyMessageIcon: 'courtesy-message--icon',
    CourtesyMessageText: 'courtesy-message--text',
    CourtesyMessageTextDate: 'courtesy-message--text-date',
    CourtesyMessageCta: 'courtesy-message--cta',
  });

const CourtesyMessage = ({
  text,
  date,
  cta,
  backgroundIcon = defaultBackgroundIcon(),
  fontSize = '22',
  colorSchema = 'light',
  cardLayout = false,
  dataSpaced = false,
}: ICourtesyMessage) => {
  const cn = classNames({ colorSchema, cardLayout, cta });

  return (
    <div className={cn.CourtesyMessage} data-spaced={dataSpaced}>
      {backgroundIcon && <div className={cn.CourtesyMessageIcon}>{backgroundIcon}</div>}
      {cta && (
        <div className={cn.CourtesyMessageCta}>
          <Cta href={cta.href} iconPosition="right" mobileButton>
            {cta.text}
          </Cta>
        </div>
      )}
      <div className={cn.CourtesyMessageText}>
        {text && (
          <Text color={colorSchema === 'dark' ? 'light' : 'dark'} fontSize={fontSize} fontWeight="400">
            {`${text} `}
          </Text>
        )}
        {date && (
          <Text
            className={cn.CourtesyMessageTextDate}
            color={colorSchema === 'dark' ? 'light' : 'dark'}
            fontSize={fontSize}
            fontWeight="500"
          >
            {date}
          </Text>
        )}
      </div>
    </div>
  );
};

export default CourtesyMessage;
